import { ConfigQuestion } from '../models/config-question.interface';
import {
	AbstractControl,
	UntypedFormArray,
	UntypedFormControl,
	UntypedFormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import {
	ConfigQuestionType,
	ERROR_MESSAGES,
	ErrorMessage,
} from '../constants/enum.const';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import * as uuid from 'uuid';
import { emailPattern } from '../constants/regex.const';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { ShowWhenRecord } from '../models/config-question-attributes.interface';

export class ConfigUtil {
	subscriptions: Subscription = new Subscription();
	unsubscribe: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	unsubscribe$: Subject<boolean> = new Subject<boolean>();
	fileControlValues: Record<string, Array<any>> = {};
	questions!: BehaviorSubject<Array<ConfigQuestion>>;
	onAnswerReceived$: Subject<{ question: ConfigQuestion; value: any }> =
		new Subject<{ question: ConfigQuestion; value: any }>();
	private _groupedComponents: { [key: string]: Array<string> } = {};

	constructor(private readonly configQuestions: Array<ConfigQuestion>) {
		this.questions = new BehaviorSubject<Array<ConfigQuestion>>(
			configQuestions
		);
		this.initializeFormControls();

		// set visibility of the questions based on their `null` value
		this.configQuestions.forEach((question) =>
			this.handleValueChange(question)
		);
	}

	private _showWhenQuestions: Map<string, any> = new Map<string, any>();

	get showWhenQuestions(): Map<string, any> {
		return this._showWhenQuestions;
	}

	private _validatorQuestions: Record<string, Array<ConfigQuestion>> = {};

	get validatorQuestions(): Record<string, Array<ConfigQuestion>> {
		return this._validatorQuestions;
	}

	private _validators: { [key: string]: Array<ValidatorFn> } = {};

	get validators(): { [p: string]: Array<ValidatorFn> } {
		return this._validators;
	}

	private _formGroup!: UntypedFormGroup;

	get formGroup(): UntypedFormGroup {
		return this._formGroup;
	}

	private _visibleControls: Set<string> = new Set<string>();

	get visibleControls(): Set<string> {
		return this._visibleControls;
	}

	private _requiredControls: Set<string> = new Set<string>();

	get requiredControls(): Set<string> {
		return this._requiredControls;
	}

	setDefaultValues(stepData: Record<string, any>): void {
		const _questions = this.questions.value;
		for (let index = 0; index < this.questions.value.length; index++) {
			let question = this.questions.value[index];
			if (stepData) {
				try {
					let skipSetValue = false;
					switch (question.type) {
						case ConfigQuestionType.COMPONENT_GROUP:
							_questions[index] = this.autoGenerateFor(question);
							this.questions.next(_questions);
							break;
						case ConfigQuestionType.FILE:
							if (stepData[question.key]) {
								if (
									!stepData[question.key].hasOwnProperty(
										'skipped'
									)
								) {
									this.fileControlValues[question.key] =
										stepData[question.key];
									this.updateFileControl(question.key);
								}
							}
							break;
						case ConfigQuestionType.URL_LIST:
							this.hydrateUrlList(
								question,
								stepData[question.key]
							);
							skipSetValue = true;
							break;
					}
					// A currency answer is a group of two controls, and setValue demands a
					// value for every one of them, so restoring a question the client has
					// not reached yet would throw. patchValue takes whatever is stored.
					if (!skipSetValue) {
						// A currency answer is a group of two controls, and setValue demands a
						// value for every one of them, so restoring a question the client has
						// not reached yet would throw. patchValue takes whatever is stored.
						this.formGroup
							?.get(question.key)
							?.patchValue(
								this.storedValueOf(
									question,
									stepData[question.key]
								),
								{
									emitEvent: false,
								}
							);
					}
				} catch (e) {
					console.error(e);
				}
			}

			this.handleValueChange(question);
		}
	}

	/**
	 * A question that accepts several answers was once answered with a single one, and a
	 * multiple selection rejects anything but a list. Reading such an answer back therefore
	 * wraps it, so an application started before the question changed still opens.
	 */
	private storedValueOf(question: ConfigQuestion, value: unknown): unknown {
		if (
			!question.attributes?.multiple ||
			value === undefined ||
			value === null ||
			Array.isArray(value)
		) {
			return value;
		}

		return [value];
	}

	handleValueChange(question: ConfigQuestion): void {
		this.handleComponentShowHide(question);
		this.handleComponentAutoGeneration(question);
		this.validatorQuestions[question.key]?.forEach(
			(_question: ConfigQuestion) => {
				this._validators[_question.key] =
					this.constructValidators(_question);
				this.applyValidatorsTo(_question);
			}
		);
	}

	onAnswerChange(question: ConfigQuestion, value: any): void {
		this.handleValueChange(question);
		if (typeof value !== 'boolean') {
			if (
				value === undefined ||
				value === null ||
				(Array.isArray(value) && !value.length) ||
				(!Array.isArray(value) && Object.values(value).length) === 0
			) {
				value = undefined;
			}
		}
		if (
			this.formGroup.get(question.key)?.valid ||
			value === undefined ||
			question.type === ConfigQuestionType.FILE
		) {
			this.onAnswerReceived$.next({ question, value: value ?? null });
		}
	}

	getErrorMessage(formControlName: string): ErrorMessage | undefined {
		const control = this.validatedControlOf(formControlName);
		const errored = this.firstErroredControl(control);
		if (errored?.touched && errored.errors) {
			// Using a loop to get the first index O(1)
			for (let key in errored.errors) {
				const override =
					this.findQuestion(formControlName)?.validators
						?.errorMessages?.[key];

				if (override) {
					return override;
				}
				switch (key) {
					case 'min':
						return ERROR_MESSAGES.MIN;
					case 'max':
						return ERROR_MESSAGES.MAX;
					case 'required':
						return ERROR_MESSAGES.REQUIRED;
					case 'requiredtrue':
						return ERROR_MESSAGES.REQUIRED_TRUE;
					case 'email':
						return ERROR_MESSAGES.EMAIL;
					case 'minlength':
						return ERROR_MESSAGES.MIN_LENGTH;
					case 'maxlength':
						return ERROR_MESSAGES.MAX_LENGTH;
					case 'pattern':
						return ERROR_MESSAGES.PATTERN;
					case 'matchesComponentValue':
						return ERROR_MESSAGES.MATCHES_COMPONENT_VALUE;
					case 'notBeforeComponentValue': {
						const label = errored.errors[key]?.label;
						return label
							? `Cannot be before ${label}`
							: ERROR_MESSAGES.NOT_BEFORE_COMPONENT_VALUE;
					}
					case 'notAboveComponentValue': {
						const label = errored.errors[key]?.label;
						return label
							? `Cannot be greater than ${label}`
							: ERROR_MESSAGES.NOT_ABOVE_COMPONENT_VALUE;
					}
					case 'requiredIfComponentHasOneOf':
						return ERROR_MESSAGES.REQUIRED;
					case 'mobileNumber':
						return ERROR_MESSAGES.PATTERN;
					case 'amount':
						return ERROR_MESSAGES.AMOUNT;
					case 'walletListDuplicate':
						return ERROR_MESSAGES.WALLET_LIST_DUPLICATE;
				}
				// Exiting the loop since we only want the first key
				break;
			}
		}
		return undefined;
	}

	private firstErroredControl(
		control: AbstractControl | null
	): AbstractControl | null {
		if (!control) {
			return null;
		}
		if (control instanceof UntypedFormArray) {
			return (
				control.controls.find(
					(child) => child.touched && child.errors
				) ?? null
			);
		}
		return control;
	}

	getFormValuesMap(): Record<string, any> {
		const values: Record<string, any> = {};
		const formValue = this.formGroup.value;

		for (let question of this.questions.value.filter((item) =>
			this.visibleControls.has(item.key)
		)) {
			values[question.key] = formValue[question.key];

			switch (question.type) {
				case ConfigQuestionType.FILE:
					if (!formValue[question.key]?.hasOwnProperty('skipped')) {
						values[question.key] =
							this.fileControlValues[question.key];
					}
					break;
				case ConfigQuestionType.URL_LIST: {
					const rows = Array.isArray(formValue[question.key])
						? formValue[question.key]
						: [];
					values[question.key] = rows
						.map((value: unknown) =>
							typeof value === 'string' ? value.trim() : ''
						)
						.filter((value: string) => !!value);
					break;
				}
			}
		}

		return values;
	}

	initializeFormControls(): void {
		this._formGroup = new UntypedFormGroup({});
		const _questions = [...this.questions.value];

		for (let question of _questions) {
			switch (question.type) {
				case ConfigQuestionType.CURRENCY:
					this.formGroup.addControl(
						question.key,
						new UntypedFormGroup({
							currency: new UntypedFormControl(),
							amount: new UntypedFormControl(),
						})
					);
					break;
				case ConfigQuestionType.COMPONENT_GROUP:
					const associatedComponent =
						question.attributes?.autoGenerateFor;
					if (!associatedComponent) {
						break;
					}
					if (!this._groupedComponents[associatedComponent]) {
						this._groupedComponents[associatedComponent] = [];
					}
					this._groupedComponents[associatedComponent] = [
						...this._groupedComponents[associatedComponent],
						question.key,
					];
					const questionIndex = this.questions.value.findIndex(
						(item) => item.key === question.key
					);
					_questions[questionIndex] = this.autoGenerateFor(question);
					break;
				case ConfigQuestionType.CHECKLIST:
					const checklistGroup: UntypedFormGroup =
						new UntypedFormGroup({});
					if (!question.options) {
						throw new Error(
							`Please ensure that options are provided for type ${question.type}`
						);
					}

					for (let option of question.options) {
						if ('key' in option && option.key) {
							checklistGroup.addControl(
								option.key,
								new UntypedFormControl(undefined)
							);
						} else {
							throw new Error(
								`[key] is required for option in the checklist`
							);
						}
					}

					this.formGroup.addControl(question.key, checklistGroup);
					break;
				case ConfigQuestionType.URL_LIST:
					this.formGroup.addControl(
						question.key,
						new UntypedFormArray([
							this.createUrlListControl(question),
						])
					);
					break;
				default:
					this.formGroup.addControl(
						question.key,
						new UntypedFormControl(undefined)
					);
					break;
			}

			if (question.attributes?.showWhen) {
				Object.keys(question.attributes?.showWhen).forEach((key) => {
					this._showWhenQuestions.set(key, {
						...this._showWhenQuestions.get(key),
						[question.key]: question,
					});
				});
			}

			if (question.validators?.requiredIfComponentHasOneOf) {
				const _value =
					this.validatorQuestions[
						question.validators?.requiredIfComponentHasOneOf
							.controlKey
					] ?? [];
				this._validatorQuestions[
					question.validators?.requiredIfComponentHasOneOf.controlKey
				] = [..._value, question];
			}

			if (question.validators?.notBeforeComponentValue) {
				const _controlKey =
					question.validators?.notBeforeComponentValue.controlKey;
				const _value = this.validatorQuestions[_controlKey] ?? [];

				this._validatorQuestions[_controlKey] = [..._value, question];
			}

			if (question.validators?.notAboveComponentValue) {
				const _controlKey =
					question.validators?.notAboveComponentValue.controlKey;
				const _value = this.validatorQuestions[_controlKey] ?? [];

				this._validatorQuestions[_controlKey] = [..._value, question];
			}

			this._validators[question.key] = this.constructValidators(question);

			this.applyValidatorsTo(question);

			this._visibleControls.add(question.key);

			if (question.validators?.required) {
				this._requiredControls.add(question.key);
			}

			if (question.attributes?.disabled) {
				this.formGroup.get(question.key)?.disable();
			}

			this.formGroup
				.get(question.key)
				?.valueChanges.pipe(
					takeUntil(this.unsubscribe$),
					tap(() => this.handleValueChange(question))
				)
				.subscribe();
		}
		this.questions.next(_questions);
	}

	private handleComponentAutoGeneration(question: ConfigQuestion): void {
		const _components = this._groupedComponents[question.key];
		if (_components) {
			const _questions = this.questions.value;
			for (let component of _components) {
				const questionIndex = this.questions.value.findIndex(
					(item) => item.key === component
				);
				if (_questions[questionIndex]) {
					_questions[questionIndex] = this.autoGenerateFor(
						_questions[questionIndex]
					);
				}
			}
			this.questions.next(_questions);
		}
	}

	private handleComponentShowHide(question: ConfigQuestion): void {
		const showWhen = this.showWhenQuestions.get(question.key);
		if (!showWhen) {
			return;
		}

		Object.keys(showWhen)?.forEach((_key) => {
			if (this.shouldShow(showWhen[_key], question)) {
				this.applyValidatorsTo(showWhen[_key]);
				this.formGroup.get(_key)?.updateValueAndValidity();
				this.visibleControls.add(_key);
			} else {
				this.clearValidatorsOf(showWhen[_key]);
				this.formGroup.get(_key)?.updateValueAndValidity();
				this.formGroup.get(_key)?.reset();
				this.visibleControls.delete(_key);
			}
		});
	}

	/**
	 * A currency answer is a group of an amount and a currency, so the two halves
	 * need different rules: presence has to hold for both, while min, max, pattern
	 * and the cross-field comparisons all describe the amount alone.
	 */
	private applyValidatorsTo(question: ConfigQuestion): void {
		if (question.type === ConfigQuestionType.URL_LIST) {
			const control = this.formGroup.get(question.key);
			if (control instanceof UntypedFormArray) {
				control.controls.forEach((row) => {
					row.setValidators(this.validators[question.key]);
					row.updateValueAndValidity({ emitEvent: false });
				});
			}
			return;
		}

		const validated = this.validatedControlOf(question.key);
		validated?.setValidators(this.validators[question.key]);
		validated?.updateValueAndValidity({ emitEvent: false });

		if (question.type !== ConfigQuestionType.CURRENCY) {
			return;
		}

		const currency = this.formGroup.get(question.key)?.get('currency');
		currency?.setValidators(
			question.validators?.required ? [Validators.required] : []
		);
		currency?.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * The control a question's rules belong to. For a money question that is the
	 * amount half, which is also where its failures are then reported, rather than
	 * the group the question is addressed by.
	 */
	private validatedControlOf(
		formControlName: string
	): AbstractControl | null {
		const control = this.formGroup.get(formControlName);

		return this.findQuestion(formControlName)?.type ===
			ConfigQuestionType.CURRENCY
			? control?.get('amount') ?? null
			: control;
	}

	private clearValidatorsOf(question: ConfigQuestion): void {
		const control = this.formGroup.get(question.key);

		if (question.type === ConfigQuestionType.URL_LIST) {
			if (control instanceof UntypedFormArray) {
				control.controls.forEach((row) => {
					row.clearValidators();
					row.updateValueAndValidity({ emitEvent: false });
				});
			}
			return;
		}

		if (question.type !== ConfigQuestionType.CURRENCY) {
			control?.clearValidators();
			return;
		}

		control?.get('amount')?.clearValidators();
		control?.get('currency')?.clearValidators();
	}

	createUrlListControl(
		question: ConfigQuestion,
		value: string = ''
	): UntypedFormControl {
		return new UntypedFormControl(
			value,
			this.validators[question.key] ??
				this.constructValidators(question)
		);
	}

	private hydrateUrlList(
		question: ConfigQuestion,
		raw: unknown
	): void {
		const urls = (
			Array.isArray(raw)
				? raw
				: typeof raw === 'string' && raw
				? [raw]
				: ['']
		)
			.map((value) => (typeof value === 'string' ? value : ''))
			.filter((value, index, all) => value || all.length === 1);

		const rows = urls.length ? urls : [''];
		const existing = this.formGroup.get(question.key);

		if (existing instanceof UntypedFormArray) {
			const disabled = existing.disabled;
			existing.clear({ emitEvent: false });
			for (const url of rows) {
				const row = this.createUrlListControl(question, url);
				if (disabled) {
					row.disable({ emitEvent: false });
				}
				existing.push(row, { emitEvent: false });
			}
			return;
		}

		const created = new UntypedFormArray(
			rows.map((url) => this.createUrlListControl(question, url))
		);
		if (this.formGroup.disabled) {
			created.disable({ emitEvent: false });
		}
		this.formGroup.setControl(question.key, created, { emitEvent: false });
	}

	private constructValidators(question: ConfigQuestion): Array<ValidatorFn> {
		const _validators = [];
		// Compared against null rather than tested for truth, so that a bound of zero
		// still reaches the control. Zero is the bound most of these questions carry.
		if (question.validators?.min != null) {
			_validators.push(Validators.min(question.validators?.min));
		}
		if (question.validators?.max != null) {
			_validators.push(Validators.max(question.validators?.max));
		}
		if (question.type === ConfigQuestionType.WALLET_LIST) {
			_validators.push(
				this.walletListValidator(!!question.validators?.required)
			);
		} else if (question.validators?.required) {
			_validators.push(Validators.required);
		}
		if (question.validators?.requiredTrue) {
			_validators.push(Validators.requiredTrue);
		}
		if (question.validators?.email) {
			_validators.push(Validators.pattern(emailPattern));
		}
		if (question.validators?.minLength) {
			_validators.push(
				Validators.minLength(question.validators?.minLength)
			);
		}
		if (question.validators?.maxLength) {
			_validators.push(
				Validators.maxLength(question.validators?.maxLength)
			);
		}
		if (question.validators?.pattern) {
			_validators.push(Validators.pattern(question.validators?.pattern));
		}
		if (question.validators?.matchesComponentValue) {
			_validators.push(
				this.matchesComponentValueValidator(
					question.validators?.matchesComponentValue
				)
			);
		}
		if (question.validators?.requiredIfComponentHasOneOf) {
			_validators.push(
				this.requiredIfComponentHasOneOf(
					question.validators?.requiredIfComponentHasOneOf.controlKey,
					question.validators?.requiredIfComponentHasOneOf
						.compareValues,
					question.validators?.requiredIfComponentHasOneOf
						.compareField
				)
			);
		}
		if (question.validators?.notBeforeComponentValue) {
			_validators.push(
				this.notBeforeComponentValueValidator(
					question.validators?.notBeforeComponentValue.controlKey
				)
			);
		}
		if (question.validators?.notAboveComponentValue) {
			_validators.push(
				this.notAboveComponentValueValidator(
					question.validators?.notAboveComponentValue.controlKey
				)
			);
		}
		if (question.type === ConfigQuestionType.MOBILE) {
			_validators.push(this.mobileNumberValidator());
		}
		if (question.type === ConfigQuestionType.CURRENCY) {
			_validators.push(this.amountValidator());
		}

		return _validators;
	}

	private shouldShow(
		component: any,
		linkedComponent: ConfigQuestion
	): boolean {
		const componentShowWhen: ShowWhenRecord =
			component.attributes.showWhen[linkedComponent.key];

		if (component.attributes.matchConditions === 'SOME') {
			return Object.keys(componentShowWhen).some((key: string) =>
				this.shouldShowCondition(
					key,
					linkedComponent,
					componentShowWhen
				)
			);
		}

		return Object.keys(componentShowWhen).every((key: string) =>
			this.shouldShowCondition(key, linkedComponent, componentShowWhen)
		);
	}

	private shouldShowCondition(
		key: string,
		linkedComponent: ConfigQuestion,
		showWhenRecord: ShowWhenRecord
	): boolean {
		const value = this.formGroup.get(linkedComponent.key)?.value;
		switch (key) {
			case 'isNotEmpty':
				return !!this.formGroup.get(linkedComponent.key)?.value;
			case 'hasValue':
				return this.hasValue(
					this.formGroup.get(linkedComponent.key)?.value,
					showWhenRecord.hasValue
				);
			case 'hasValueOtherThan':
				return !this.hasValue(
					this.formGroup.get(linkedComponent.key)?.value,
					showWhenRecord.hasValueOtherThan
				);
			case 'hasValueAtPath':
				if (!showWhenRecord.hasValueAtPath) {
					return false;
				}
				const _hasValuePath = showWhenRecord.hasValueAtPath?.path;

				if (
					Array.isArray(
						this.formGroup.get(linkedComponent.key)?.value
					)
				) {
					return this.formGroup
						.get(linkedComponent.key)
						?.value?.some(
							(item: any) =>
								item[_hasValuePath] ===
								showWhenRecord.hasValueAtPath?.value
						);
				}

				return (
					this.formGroup.get(linkedComponent.key)?.value?.[
						_hasValuePath
					] === showWhenRecord.hasValueAtPath?.value
				);
			case 'hasValueAtPathOtherThan': {
				const rule = showWhenRecord.hasValueAtPathOtherThan;
				if (!rule) {
					return false;
				}

				const controlValue = this.formGroup.get(
					linkedComponent.key
				)?.value;

				// Not selected yet -> keep hidden (so neither field shows by default)
				if (controlValue == null) {
					return false;
				}

				const differsAtPath = (
					item: Record<string, unknown>
				): boolean =>
					item?.[rule.path] != null && item[rule.path] !== rule.value;

				if (Array.isArray(controlValue)) {
					return controlValue.some((item: Record<string, unknown>) =>
						differsAtPath(item)
					);
				}

				return differsAtPath(controlValue as Record<string, unknown>);
			}
			case 'hasOneOf':
				if (!showWhenRecord.hasOneOf?.length) {
					return false;
				}
				if (!value || !value?.length) {
					return false;
				}

				return this.hasOneOf(value, showWhenRecord.hasOneOf);
			case 'hasNoneOf':
				if (!showWhenRecord.hasNoneOf?.length) {
					return false;
				}
				if (!value || !value?.length) {
					return false;
				}

				return !this.hasOneOf(value, showWhenRecord.hasNoneOf);
			case 'greaterThan':
				if (!showWhenRecord.greaterThan) {
					return false;
				}
				switch (linkedComponent.type) {
					case ConfigQuestionType.DATE:
						return (
							new Date(
								this.formGroup.get(linkedComponent.key)?.value
							) > new Date(showWhenRecord.greaterThan)
						);
					default:
						return (
							this.formGroup.get(linkedComponent.key)?.value >
							showWhenRecord.greaterThan
						);
				}
			default:
				console.error('Unhandled condition [%s]', key);
				return false;
		}
	}

	private autoGenerateFor(question: ConfigQuestion): ConfigQuestion {
		const _question: ConfigQuestion = { ...question };
		if (!_question.componentTemplate) {
			throw new Error('[componentTemplate] is required');
		}
		if (!_question.attributes?.autoGenerateFor) {
			throw new Error('[attributes].[autoGenerateFor] is required');
		}

		const associatedControl = this.formGroup.get(
			_question.attributes?.autoGenerateFor
		);
		if (
			!associatedControl?.value ||
			!Array.isArray(associatedControl?.value)
		) {
			return _question;
		}

		this.formGroup.removeControl(_question.key);
		this.formGroup.addControl(_question.key, new UntypedFormGroup({}));
		let group = this.formGroup.get(_question.key) as UntypedFormGroup;

		if (!_question.componentList) {
			_question.componentList = {};
		}

		for (let [index, value] of associatedControl?.value.entries()) {
			const associatedQuestionIndex = this.questions.value.findIndex(
				(item) => item.key === _question.attributes?.autoGenerateFor
			);
			const key = uuid.v5(
				String(index),
				this.questions.value[associatedQuestionIndex].key
			);

			const template = { ..._question.componentTemplate, key };

			switch (this.questions.value[associatedQuestionIndex]?.type) {
				case ConfigQuestionType.COUNTRY_SELECT:
					template.label = value.name;
					break;
				default:
					template.label = value.label;
					break;
			}

			group.addControl(
				key,
				new UntypedFormControl(
					undefined,
					this.constructValidators(template)
				)
			);

			_question.componentList[key] = template;
		}
		if (Object.keys(group.value)?.length < 1) {
			this._visibleControls.delete(_question.key);
		}
		return _question;
	}

	private updateFileControl(key: string): void {
		const control = this.formGroup.get(key);
		const files = this.fileControlValues[key];
		control?.setValue(files);
	}

	private matchesComponentValueValidator(controlKey: string): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const controlValue = this.formGroup.get(controlKey)?.value;
			return control.value !== controlValue
				? { matchesComponentValue: { value: control.value } }
				: null;
		};
	}

	private notBeforeComponentValueValidator(controlKey: string): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const boundary = this.formGroup.get(controlKey)?.value;
			if (!control.value || !boundary) {
				return null;
			}
			const value = new Date(control.value).getTime();
			const limit = new Date(boundary).getTime();
			if (isNaN(value) || isNaN(limit)) {
				return null;
			}
			return value < limit
				? {
						notBeforeComponentValue: {
							value: control.value,
							label: this.findQuestion(controlKey)?.label,
						},
				  }
				: null;
		};
	}

	private notAboveComponentValueValidator(controlKey: string): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = this.comparableNumber(control.value);
			const boundary = this.comparableNumber(
				this.formGroup.get(controlKey)?.value
			);

			if (value === undefined || boundary === undefined) {
				return null;
			}

			return value > boundary
				? {
						notAboveComponentValue: {
							value: control.value,
							label: this.findQuestion(controlKey)?.label,
						},
				  }
				: null;
		};
	}

	/**
	 * The figure to compare lives one level down for a money question, whose value is
	 * a group of an amount and a currency, and at the top level for a plain number.
	 */
	private comparableNumber(value: unknown): number | undefined {
		const amount =
			typeof value === 'object' && value !== null
				? (value as { amount?: unknown }).amount
				: value;

		if (amount === null || amount === undefined || amount === '') {
			return undefined;
		}

		const parsed = Number(amount);

		return isNaN(parsed) ? undefined : parsed;
	}

	private findQuestion(key: string): ConfigQuestion | undefined {
		for (const question of this.configQuestions) {
			if (question.key === key) {
				return question;
			}

			const nested = question.componentList?.[key];
			if (nested) {
				return nested;
			}
		}

		return undefined;
	}

	private requiredIfComponentHasOneOf(
		controlKey: string,
		compareValues: Array<string>,
		compareField?: string
	): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const controlValue = this.formGroup.get(controlKey)?.value;
			return !control.value &&
				compareValues.includes(
					compareField
						? controlValue && controlValue[compareField]
						: controlValue
				)
				? { requiredIfComponentHasOneOf: true }
				: null;
		};
	}

	/**
	 * Whether a money amount is a figure at all, the currency component having already
	 * removed the client's separators. Only the shape is judged here: a negative or an
	 * oversized amount is a number, and is left to the question's own bounds to report.
	 */
	private amountValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const amount = control.value;

			if (amount === null || amount === undefined || amount === '') {
				return null;
			}

			return /^-?\d+(\.\d+)?$/.test(`${amount}`)
				? null
				: { amount: { value: amount } };
		};
	}

	private mobileNumberValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			return control.value &&
				typeof control.value === 'string' &&
				!isValidPhoneNumber(control.value)
				? { mobileNumber: { value: control.value } }
				: null;
		};
	}

	/**
	 * Declared wallets need at least one complete row, and the same
	 * network+address pair must not appear twice.
	 */
	private walletListValidator(required: boolean): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!Array.isArray(value) || value.length === 0) {
				return required ? { required: true } : null;
			}

			const incomplete = value.some(
				(row: { network?: string; address?: string }) =>
					!row?.network || !String(row?.address ?? '').trim()
			);
			if (incomplete) {
				return { required: true };
			}

			const keys = value.map(
				(row: { network: string; address: string }) =>
					`${row.network}::${String(row.address).trim().toLowerCase()}`
			);
			if (new Set(keys).size !== keys.length) {
				return { walletListDuplicate: true };
			}

			return null;
		};
	}

	private hasValue(controlValue: any, value: any): boolean {
		if (Array.isArray(controlValue)) {
			return controlValue
				.map((item) => item?.value ?? item)
				.includes(value);
		}
		if (controlValue?.value) {
			return controlValue?.value === value;
		}
		return controlValue === value;
	}

	private hasOneOf(controlValue: any, values: Array<any>): boolean {
		return (
			values.some((value: any) => {
				if (!controlValue || !controlValue?.length) {
					return false;
				}

				if (Array.isArray(controlValue)) {
					return controlValue
						.map((item) => item.value ?? item)
						.includes(value);
				}
				return (controlValue?.value ?? controlValue) === value;
			}) ?? false
		);
	}
}
